import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { XTinyMCEPlugin } from '@adornis/writer/x-tinymce.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { KpfFilter } from '@chancenwerk/shared/filter/KpfFilter.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { School } from '@chancenwerk/shared/master-data-management/partner/entities/School.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ExternalContactPerson } from '@chancenwerk/shared/master-data-management/users/entities/ExternalContactPerson.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { RaumRoutes } from '@chancenwerk/routes/raumRoutes.js';
import { firstValueFrom } from 'rxjs';

const makeLinkToArbitraryEntity = (entity: MongoEntity) => {
  switch (entity._class) {
    case KPF._class:
      return `${projectConfig.get('RAUM_URL').slice(0, -1)}${RaumRoutes.editKpf}/${entity._id}`;
    case School._class:
      return `${projectConfig.get('RAUM_URL').slice(0, -1)}${RaumRoutes.editPartner}/${entity._id}`;
    case Mitarbeiter._class:
    case ExternerMitarbeiter._class:
    case VerwaltungsMitarbeiter._class:
      return `${projectConfig.get('RAUM_URL').slice(0, -1)}${RaumRoutes.editEmployee}/${entity._id}`;
    case Student._class:
      return `${projectConfig.get('RAUM_URL').slice(0, -1)}${RaumRoutes.editStudent}/${entity._id}`;
    case ExternalContactPerson._class:
      return `#?externalPerson=${entity._id}`;
    default:
      throw new Error('unsupported class: ' + entity._class);
  }
};

export const taggerTinyMCEPlugin: XTinyMCEPlugin = {
  name: 'cwTagger',
  setup: (editor, url) => {
    editor.ui.registry.addAutocompleter('Entity Autocompleter', {
      ch: '@',
      async fetch(pattern, maxResults, fetchOptions) {
        const kpfs = await firstValueFrom(
          KPFAPI.subscribeByFilter(
            0,
            10,
            new KpfFilter({ search: pattern }),
          )({ produkt: { kuerzel: 1 }, school: { kuerzel: 1, name: 1 } }),
        );
        const schulen = await School.findEntityByText<School>(
          School._class,
          ['strasse', 'bundesland', 'schulform', 'zipCity.city', 'zipCity.zip', 'name', 'kuerzel'],
          pattern,
          'i',
        )({
          _id: 1,
          _class: 1,
          name: 1,
          kuerzel: 1,
          address: { strasse: 1, careOf: 1, country: 1, zipCity: { zip: 1, city: 1 } },
        });
        const mitarbeiter = await firstValueFrom(
          UserAPI.subscribeByFilter(
            0,
            10,
            new ChancenwerkUserFilter({ search: pattern }),
          )({ nachname: 1, vorname: 1, email: 1, adresse: { zipCity: { city: 1 } } }),
        );
        const schueler = await firstValueFrom(
          UserAPI.subscribeByFilter(
            0,
            10,
            new ChancenwerkUserFilter({ search: pattern }),
          )({ nachname: 1, vorname: 1, email: 1, adresse: { zipCity: { city: 1 } } }),
        );
        const externalPersons = await firstValueFrom(
          UserAPI.subscribeByFilter(
            0,
            10,
            new ChancenwerkUserFilter({ search: pattern }),
          )({ nachname: 1, vorname: 1, email: 1 }),
        );

        const buildHTML = (entityClass: string, id: string, link: string, text: string) =>
          ` <a data-type="${entityClass}" data-id="${id}" href="${link}" style="background: ${
            DesignSystem.currentTheme.getValue().colors.accent
          }; color: ${DesignSystem.currentTheme.getValue().colors.white}; padding: 0 ${
            DesignSystem.currentTheme.getValue().spacing.xs
          }; border-radius: 5px;">${text}</a>  `;

        return [
          { type: 'separator', text: 'KPFs' },
          ...kpfs.map(kpf => ({
            type: 'autocompleteitem' as const,
            value: buildHTML(KPF._class, kpf._id, makeLinkToArbitraryEntity(kpf), kpf.kuerzel),
            text: kpf.kuerzel,
          })),

          { type: 'separator', text: 'Schulen' },
          ...schulen.map(s => ({
            type: 'autocompleteitem' as const,
            value: buildHTML(School._class, s._id, makeLinkToArbitraryEntity(s as School), s.name ?? ''),
            text: `${s.name} in ${s.address?.zipCity?.city}`,
          })),

          { type: 'separator', text: 'Mitarbeitende' },
          ...mitarbeiter.map(ma => ({
            type: 'autocompleteitem' as const,
            value: buildHTML(
              Mitarbeiter._class,
              ma._id,
              makeLinkToArbitraryEntity(ma),
              `${ma.vorname ?? 'Vorname'} ${ma.nachname ?? 'Nachname'}`,
            ),
            text: `${ma.vorname ?? 'Vorname'} ${ma.nachname ?? 'Nachname'} aus ${ma.adresse?.zipCity?.city ?? ''}`,
          })),

          { type: 'separator', text: 'Externe' },
          ...externalPersons.map(p => ({
            type: 'autocompleteitem' as const,
            value: buildHTML(ExternalContactPerson._class, p._id, makeLinkToArbitraryEntity(p), p.toString()),
            text: `${p.toString()} (${p.email})`,
          })),

          { type: 'separator', text: 'Schüler:innen' },
          ...schueler.map(s => ({
            type: 'autocompleteitem' as const,
            value: buildHTML(
              Student._class,
              s._id,
              makeLinkToArbitraryEntity(s),
              `${s.vorname ?? ''} ${s.nachname ?? ''}`,
            ),
            text: s.vorname + ' ' + s.nachname + ' aus ' + s.adresse?.zipCity?.city,
          })),
        ];
      },
      onAction(autocompleterApi, rng, value, meta) {
        editor.selection.setRng(rng);
        editor.insertContent(value);
      },
    });
    return { getMetadata: () => ({ name: 'ChancenWerk Tagger', url: 'https://adornis.de/cw-tagger' }) };
  },
};
